import type {
  CallGroup,
  CallGroupPayload,
  CallGroupUser
} from '@/interfaces/CallGroup.interface';
import type { UserInterface } from '@/interfaces/User.interface';

export function callGroupMemberIds(
  group?: Pick<CallGroup, 'users'> | null
): number[] {
  return (group?.users ?? [])
    .map((user) => Number(user.user_id ?? user.id))
    .filter((id) => Number.isInteger(id) && id > 0);
}

export function buildCallGroupPayload(
  fields: {
    name: string;
    number: string;
    pipeline_id?: number | string | null;
  },
  userIds: number[] = []
): CallGroupPayload {
  return {
    name: fields.name.trim(),
    number: fields.number.trim(),
    // Sent explicitly rather than omitted, so clearing the pipeline actually clears it.
    pipeline_id: fields.pipeline_id ? Number(fields.pipeline_id) : null,
    user_ids: userIds
  };
}

export function callGroupMemberCount(
  group: Pick<CallGroup, 'users_count' | 'users'>
): number {
  if (typeof group.users_count === 'number') {
    return group.users_count;
  }
  return group.users?.length ?? 0;
}

export function canDeleteCallGroup(
  group: Pick<CallGroup, 'users_count' | 'users'>
): boolean {
  return callGroupMemberCount(group) === 0;
}

export function callGroupUserDisplayName(user: {
  full_name?: string;
  name?: string;
  last_name?: string;
}): string {
  if (user.full_name?.trim()) {
    return user.full_name.trim();
  }
  return [user.name, user.last_name].filter(Boolean).join(' ').trim();
}

export function mapCallGroupMembers(
  group: Pick<CallGroup, 'id' | 'users'>,
  searchTerm?: string
): CallGroupUser[] {
  const items = (group.users ?? []).map((user) => {
    const userId = String(user.user_id ?? user.id);
    return {
      ...user,
      id: userId,
      user_id: userId,
      call_group_id: String(group.id)
    };
  });

  const query = searchTerm?.trim().toLowerCase();
  if (!query) {
    return items;
  }

  return items.filter((user) => {
    const name = callGroupUserDisplayName(user).toLowerCase();
    const email = (user.email ?? '').toLowerCase();
    return name.includes(query) || email.includes(query);
  });
}

export function usersForCallGroupPicker(
  users: Array<Pick<UserInterface, 'id' | 'email' | 'name' | 'last_name' | 'full_name'>>,
  currentUser?: Pick<
    UserInterface,
    'id' | 'email' | 'name' | 'last_name' | 'full_name'
  > | null
): Array<Pick<UserInterface, 'id' | 'email' | 'name' | 'last_name' | 'full_name'>> {
  if (!currentUser?.id) {
    return users;
  }

  const currentId = String(currentUser.id);
  if (users.some((user) => String(user.id) === currentId)) {
    return users;
  }

  return [currentUser, ...users];
}
